import React, { useState, useMemo, useEffect } from 'react';
import { Tooltip } from 'antd';
import VehicleTrackingCard from './VehicleTrackingCard';
import NewTableComponent from '../../../components/NewTableComponent';
import { apiService } from '../../../services/apiService';
import { getHistoryDateRange } from './vehicleTracking.adapter';
import {
  dur,
  dt,
  clock,
  placeName,
  STATUS_HEX,
  typeIconSVG,
  odShort,
  nearName,
  dshort,
  ic,
  RUN_HEX,
  SPEED_LIMIT,
  FUEL_CONFIG,
  kmpl,
} from './trackingUtils';

const getSegStartLoc = (g, H) =>
  g?.locationStart ||
  H?.pts?.[g?.n0]?.location ||
  (H?.pts?.[g?.n0]?.p ? placeName(H.pts[g.n0].p) : (g?.p0 ? placeName(g.p0) : 'Location'));

const getSegEndLoc = (g, H) =>
  g?.locationEnd ||
  H?.pts?.[g?.nEnd]?.location ||
  (H?.pts?.[g?.nEnd]?.p ? placeName(H.pts[g.nEnd].p) : (g?.pEnd ? placeName(g.pEnd) : 'Location'));

const VehicleTrackingHistory = ({
  vehicles = [],
  focusedVehicle = null,
  historyData = null,
  fleetDataList: propFleetDataList = null,
  loading = false,
  error = null,
  onRetry = null,
  period = 'today',
  customRange = null,
  hview = 'map', // 'map' | 'list'
  onSelectVehicle,
  onShowAllVehicles,
  playbackStep = 0,
  onSeekPlayback,
  hoveredReg = null,
  onHoverReg = null,
  telemetryData = null,
  detailData = null,
  onRefreshTelemetry = null,
  onShowTripOnMap = null,
}) => {
  const [openSegIdx, setOpenSegIdx] = useState(-1);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sortCol] = useState('km');
  const [sortDir] = useState(-1);
  const [showTrackingCard, setShowTrackingCard] = useState(false);
  const [exportingHistoryPdf, setExportingHistoryPdf] = useState(false);

  const [segCurrentPage, setSegCurrentPage] = useState(1);
  const [segLimit, setSegLimit] = useState(20);

  // Focus vehicle telemetry (real API data only, no static fallback)
  const focusData = useMemo(() => {
    return historyData;
  }, [historyData]);

  // Reset segment pagination when focused vehicle changes
  useEffect(() => {
    setSegCurrentPage(1);
  }, [focusedVehicle?.id, focusedVehicle?.reg, period, customRange]);

  const enrichedSegs = useMemo(() => {
    if (!focusData?.segs) return [];
    const { v, H, segs } = focusData;
    return (segs || []).map((g, idx) => {
      const startLoc = getSegStartLoc(g, H) || '—';
      const endLoc = g.st === 'moving' ? (getSegEndLoc(g, H) || '—') : 'Same place';
      const fuelVal =
        g.st === 'moving'
          ? `${(g.km / kmpl(v)).toFixed(2)} L`
          : g.st === 'idle' && FUEL_CONFIG.idleEnabled
          ? `${(((FUEL_CONFIG.idleLph?.[v?.cat || 'HTV'] || 1.8) * g.min) / 60).toFixed(2)} L`
          : '—';

      return {
        ...g,
        _idx: idx + 1,
        _start: clock(g.t0),
        _end: clock(g.t1),
        _dur: dur(g.min),
        _dist: g.st === 'moving' ? `${Math.round(g.km)} km` : '—',
        _speed: g.st === 'moving' ? `${g.avg} / ${g.max} km/h` : '—',
        _fromLoc: startLoc,
        _toLoc: endLoc,
        _ign: g.st === 'stopped' ? 'Off' : 'On',
        _fuel: fuelVal,
      };
    });
  }, [focusData]);

  const segTotalItems = enrichedSegs.length;
  const segTotalPages = Math.ceil(segTotalItems / segLimit) || 1;

  const paginatedSegs = useMemo(() => {
    const start = (segCurrentPage - 1) * segLimit;
    return enrichedSegs.slice(start, start + segLimit);
  }, [enrichedSegs, segCurrentPage, segLimit]);

  const handleExportVehicleHistoryPdf = async () => {
    const vId = focusedVehicle?.id || focusData?.v?.id;
    if (!vId) {
      alert('Vehicle ID is required to export journey segments PDF.');
      return;
    }
    try {
      setExportingHistoryPdf(true);
      const { from, to } = getHistoryDateRange(period, customRange);
      const res = await apiService.exportFmcrVehicleJourneySegmentsPdf({
        vehicle_id: vId,
        from,
        to,
      });
      const fileUrl = res?.data?.file_url || res?.file_url;
      if (fileUrl) {
        const link = document.createElement('a');
        const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
        const absoluteUrl = fileUrl.startsWith('http')
          ? fileUrl
          : `${baseUrl.replace(/\/api\/?$/, '')}/${fileUrl}`;
        link.href = absoluteUrl;
        link.target = '_blank';
        link.download = `vehicle-${focusedVehicle?.reg || vId}-journey-segments.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('PDF URL not returned from server.');
      }
    } catch (err) {
      console.error('Vehicle Journey Segments PDF Export failed:', err);
      alert(typeof err === 'string' ? err : 'Failed to export vehicle journey PDF');
    } finally {
      setExportingHistoryPdf(false);
    }
  };

  // Fleet telemetry (computed from real API vehicles or passed propFleetDataList)
  const fleetDataList = useMemo(() => {
    if (Array.isArray(propFleetDataList) && propFleetDataList.length > 0) {
      return propFleetDataList;
    }
    const { from, to } = getHistoryDateRange(period, customRange);
    const fromTs = new Date(from.replace(' ', 'T')).getTime() || Date.now() - 86400000;
    const toTs = new Date(to.replace(' ', 'T')).getTime() || Date.now();
    return (vehicles || []).map((v) => ({
      v,
      km: v.km || 0,
      mv: v.st === 'moving' ? 1 : 0,
      idle: v.st === 'idle' ? 1 : 0,
      stp: v.st === 'stopped' ? 1 : 0,
      stops: v.stops?.length || 0,
      endSt: v.st || 'stopped',
      endP: v.lng && v.lat ? [v.lng, v.lat] : [67.0, 24.8],
      trips: v.trip ? [v.trip] : [],
      custs: v.cust ? [v.cust] : [],
      avg: v.spd || 0,
      max: v.spd || 0,
      fuel: v.km ? Math.round(v.km / 3.5) : 0,
      vio: 0,
      runs: [],
      segs: [],
      H: {
        from: fromTs,
        to: toTs,
        pts: v.lng && v.lat ? [{ p: [v.lng, v.lat], t: v.last, spd: v.spd, ign: v.ignition, location: v.loc }] : [],
      },
    }));
  }, [propFleetDataList, vehicles, period, customRange]);

  // Handle sorting and pagination for fleet listing
  const sortedFleetList = useMemo(() => {
    return [...fleetDataList].sort((a, b) => {
      let x = sortCol === 'reg' ? (a.v?.reg || '') : sortCol === 'drv' ? (a.v?.drv || '') : (a[sortCol] ?? 0);
      let y = sortCol === 'reg' ? (b.v?.reg || '') : sortCol === 'drv' ? (b.v?.drv || '') : (b[sortCol] ?? 0);
      if (typeof x === 'string') {
        return x.localeCompare(String(y)) * sortDir;
      }
      return (x > y ? 1 : x < y ? -1 : 0) * sortDir;
    });
  }, [fleetDataList, sortCol, sortDir]);

  const totalItems = sortedFleetList.length;
  const totalPages = Math.ceil(totalItems / limit) || 1;

  const paginatedFleetList = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return sortedFleetList.slice(start, start + limit);
  }, [sortedFleetList, currentPage, limit]);

  const fleetColumns = useMemo(
    () => [
      {
        name: 'Vehicle',
        selector: (row) => row?.v?.reg || '',
        minWidth: '200px',
        cell: (row) => {
          const v = row.v || {};
          return (
            <div
              className="vh-clickable-cell"
              role="button"
              tabIndex={0}
              title="Click to view vehicle history on map"
              onClick={() => onSelectVehicle && onSelectVehicle(v.reg)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectVehicle && onSelectVehicle(v.reg);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: 0,
                margin: 0,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
            >
              <span
                className="vt-ic"
                dangerouslySetInnerHTML={{
                  __html: typeIconSVG(v.type, row.endSt || v.st || 'stopped'),
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span
                    className="vh-reg-link"
                    style={{ fontWeight: 700, fontSize: '13px', color: '#1E293B', whiteSpace: 'nowrap' }}
                  >
                    {v.reg || '-'}
                  </span>
                  <span className="vcat" style={{ fontSize: '9.5px', padding: '1px 5px', lineHeight: '13px' }}>
                    {v.cat || 'LTV'}
                  </span>
                </div>
                <span style={{ fontSize: '11px', color: '#64748B', whiteSpace: 'nowrap', marginTop: '1px' }}>
                  {v.type || v.model || 'Commercial'}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        name: 'Driver',
        selector: (row) => row?.v?.drv || '',
        minWidth: '220px',
        cell: (row) => {
          const v = row.v || {};
          const drvName = v.drv || 'No Driver';
          const initials =
            drvName
              .split(' ')
              .filter(Boolean)
              .slice(0, 2)
              .map((s) => s[0].toUpperCase())
              .join('') || 'UN';
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
              <span
                className="drv-av"
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '50%',
                  background: '#EDE9FE',
                  color: '#4014B8',
                  fontWeight: 600,
                  fontSize: '11px',
                  display: 'inline-grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                {initials}
              </span>
              <span style={{ fontSize: '12.5px', color: '#334155', fontWeight: 500, whiteSpace: 'nowrap' }}>
                {drvName}
              </span>
            </div>
          );
        },
      },
      {
        name: 'Distance',
        selector: (row) => row.km || 0,
        minWidth: '120px',
        cell: (row) => (
          <span style={{ fontWeight: 700, fontSize: '13px', color: '#1E293B', whiteSpace: 'nowrap' }}>
            {Math.round(row.km || 0).toLocaleString('en-US')} km
          </span>
        ),
      },
      {
        name: 'Driving',
        selector: (row) => row.mv || 0,
        minWidth: '110px',
        cell: (row) => <span className="num" style={{ whiteSpace: 'nowrap' }}>{dur(row.mv)}</span>,
      },
      {
        name: 'Idle',
        selector: (row) => row.idle || 0,
        minWidth: '100px',
        cell: (row) => <span className="num" style={{ whiteSpace: 'nowrap' }}>{dur(row.idle)}</span>,
      },
      {
        name: 'Stopped',
        selector: (row) => row.stp || 0,
        minWidth: '110px',
        cell: (row) => <span className="num" style={{ whiteSpace: 'nowrap' }}>{dur(row.stp)}</span>,
      },
      {
        name: 'Stops',
        selector: (row) => row.stops || 0,
        minWidth: '90px',
        cell: (row) => <span className="num" style={{ whiteSpace: 'nowrap' }}>{row.stops || 0}</span>,
      },
      {
        name: 'Violations',
        selector: (row) => row.vio || 0,
        minWidth: '100px',
        cell: (row) =>
          row.vio > 0 ? (
            <span className="vpill vio" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
              {row.vio}
            </span>
          ) : (
            <span style={{ color: '#94A3B8', whiteSpace: 'nowrap' }}>0</span>
          ),
      },
      {
        name: 'Trips',
        minWidth: '260px',
        cell: (row) => {
          const trips = row.trips || [];
          if (!trips.length) {
            return (
              <span
                style={{
                  display: 'inline-block',
                  color: '#94A3B8',
                  fontSize: '11.5px',
                  fontStyle: 'italic',
                }}
              >
                — No trip —
              </span>
            );
          }
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '1px 0' }}>
              {trips.map((t, tIdx) => {
                const isDone =
                  t.status === 'completed' || t.status === 'done' || t.st === 'done';
                const fullRoute =
                  t.route ||
                  (t.from && t.to
                    ? `${t.from} → ${t.to}`
                    : t.trip_no || `Trip #${t.id || tIdx + 1}`);

                let shortRoute = fullRoute;
                if (t.from && t.to) {
                  const clean = (s) =>
                    String(s).replace(/\.+$/, '').split(',')[0].trim();
                  shortRoute = `${clean(t.from)} → ${clean(t.to)}`;
                } else if (t.route && t.route.includes('→')) {
                  const parts = t.route.split('→');
                  const clean = (s) =>
                    String(s).replace(/\.+$/, '').split(',')[0].trim();
                  shortRoute = `${clean(parts[0])} → ${clean(parts[1])}`;
                }

                return (
                  <div
                    key={tIdx}
                    style={{
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        padding: '1px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 600,
                        lineHeight: '14px',
                        background: isDone ? '#F0FDF4' : '#EFF6FF',
                        color: isDone ? '#15803D' : '#1D4ED8',
                        border: `1px solid ${isDone ? '#DCFCE7' : '#DBEAFE'}`,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span
                        style={{
                          width: '5px',
                          height: '5px',
                          borderRadius: '50%',
                          background: isDone ? '#22C55E' : '#3B82F6',
                        }}
                      />
                      {isDone ? 'Completed' : 'In Transit'}
                    </span>

                    <Tooltip title={fullRoute} placement="topLeft">
                      <span
                        style={{
                          color: '#1E293B',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '260px',
                          cursor: 'help',
                        }}
                      >
                        {shortRoute}
                      </span>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        name: 'Customer(s)',
        selector: (row) => (Array.isArray(row.custs) ? row.custs.join(', ') : '—'),
        minWidth: '140px',
        cell: (row) => (
          <span style={{ fontSize: '12.5px', color: '#475569', whiteSpace: 'nowrap' }}>
            {Array.isArray(row.custs) && row.custs.length > 0 ? row.custs.join(', ') : '—'}
          </span>
        ),
      },
      {
        name: 'Avg / Max Speed',
        selector: (row) => row.max || 0,
        minWidth: '155px',
        cell: (row) => (
          <span className="num" style={{ fontSize: '12.5px', whiteSpace: 'nowrap' }}>
            {row.avg || 0} / {row.max || 0} km/h
          </span>
        ),
      },
      {
        name: 'Fuel (Est.)',
        selector: (row) => row.fuel || 0,
        minWidth: '100px',
        cell: (row) => (
          <span className="num" style={{ fontWeight: 700, fontSize: '12.5px', whiteSpace: 'nowrap', color: row.fuel > 0 ? '#7c3aed' : '#94A3B8' }}>
            {row.fuel > 0 ? `${Number(row.fuel).toFixed(1)} L` : '—'}
          </span>
        ),
      },
      {
        name: 'First Start',
        selector: (row) => row.H?.from || 0,
        minWidth: '100px',
        cell: (row) => (
          <span className="num" style={{ fontSize: '12.5px', whiteSpace: 'nowrap', color: '#334155' }}>
            {row.H?.from ? clock(row.H.from) : '—'}
          </span>
        ),
      },
      {
        name: 'Last Position',
        selector: (row) => row.v?.loc || '',
        minWidth: '180px',
        cell: (row) => {
          const loc = row.v?.loc && row.v.loc !== 'Awaiting coordinates' ? row.v.loc : (row.endP ? `${Number(row.endP[1]).toFixed(4)}°N` : '—');
          return (
            <Tooltip title={loc} placement="topLeft">
              <span style={{ fontSize: '12px', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px', display: 'block', cursor: 'help' }}>
                {loc}
              </span>
            </Tooltip>
          );
        },
      },
      {
        name: 'Time Split',
        minWidth: '130px',
        cell: (row) => {
          const total = (row.mv || 0) + (row.idle || 0) + (row.stp || 0) || 1;
          const mvPct = ((row.mv || 0) / total * 100).toFixed(0);
          const idlePct = ((row.idle || 0) / total * 100).toFixed(0);
          const stpPct = ((row.stp || 0) / total * 100).toFixed(0);
          return (
            <div style={{ width: '100%', minWidth: '120px' }}>
              <div style={{ display: 'flex', height: '6px', borderRadius: '3px', overflow: 'hidden', gap: '1px' }}>
                <div style={{ flex: row.mv || 0, background: STATUS_HEX.moving, minWidth: row.mv ? '2px' : 0 }} title={`Driving ${dur(row.mv)}`} />
                <div style={{ flex: row.idle || 0, background: STATUS_HEX.idle, minWidth: row.idle ? '2px' : 0 }} title={`Idle ${dur(row.idle)}`} />
                <div style={{ flex: row.stp || 0, background: STATUS_HEX.stopped, minWidth: row.stp ? '2px' : 0 }} title={`Stopped ${dur(row.stp)}`} />
              </div>
              <div style={{ fontSize: '10px', color: '#64748B', marginTop: '2px', whiteSpace: 'nowrap' }}>
                {mvPct}% · {idlePct}% · {stpPct}%
              </div>
            </div>
          );
        },
      },
      {
        name: 'Action',
        minWidth: '80px',
        cell: (row) => {
          const v = row.v || {};
          return (
            <button
              type="button"
              className="icon-btn"
              title="Open on map"
              onClick={() => onSelectVehicle && onSelectVehicle(v.reg)}
            >
              <span dangerouslySetInnerHTML={{ __html: ic('map') }} />
            </button>
          );
        },
      },
    ],
    [onSelectVehicle]
  );

  const segmentColumns = useMemo(
    () => [
      {
        name: '#',
        selector: (row) => row._idx,
        width: '55px',
        cell: (row) => (
          <span className="num muted" style={{ fontSize: '12px', fontWeight: 500, color: '#64748B', whiteSpace: 'nowrap' }}>
            {row._idx}
          </span>
        ),
      },
      {
        name: 'Status',
        selector: (row) => row.st || '',
        width: '110px',
        cell: (row) => (
          <span className={`spill sp-${row.st}`} style={{ whiteSpace: 'nowrap' }}>
            {row.st ? row.st.charAt(0).toUpperCase() + row.st.slice(1) : '-'}
          </span>
        ),
      },
      {
        name: 'Start',
        selector: (row) => row.t0 || 0,
        width: '85px',
        cell: (row) => <span className="num" style={{ fontSize: '12.5px', whiteSpace: 'nowrap' }}>{row._start}</span>,
      },
      {
        name: 'End',
        selector: (row) => row.t1 || 0,
        width: '85px',
        cell: (row) => <span className="num" style={{ fontSize: '12.5px', whiteSpace: 'nowrap' }}>{row._end}</span>,
      },
      {
        name: 'Duration',
        selector: (row) => row.min || 0,
        width: '100px',
        cell: (row) => <span className="num" style={{ fontSize: '12.5px', whiteSpace: 'nowrap' }}>{row._dur}</span>,
      },
      {
        name: 'Distance',
        selector: (row) => row.km || 0,
        width: '100px',
        cell: (row) => (
          <span className="num" style={{ fontWeight: 600, fontSize: '12.5px', color: row.st === 'moving' ? '#1E293B' : '#94A3B8', whiteSpace: 'nowrap' }}>
            {row._dist}
          </span>
        ),
      },
      {
        name: 'Avg / Max Speed',
        selector: (row) => row.avg || 0,
        width: '155px',
        minWidth: '140px',
        cell: (row) => (
          <span className="num" style={{ fontSize: '12.5px', color: row.st === 'moving' ? '#1E293B' : '#94A3B8', whiteSpace: 'nowrap' }}>
            {row._speed}
          </span>
        ),
      },
      {
        name: 'From',
        selector: (row) => row._fromLoc || '',
        width: '200px',
        cell: (row) => {
          const loc = row._fromLoc || '—';
          if (!loc || loc === '—') {
            return <span style={{ color: '#94A3B8' }}>—</span>;
          }
          return (
            <div style={{ width: '100%', maxWidth: '200px', minWidth: 0, overflow: 'hidden' }}>
              <Tooltip title={loc} placement="topLeft">
                <span
                  style={{
                    display: 'block',
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '12.5px',
                    color: '#334155',
                    cursor: 'pointer',
                  }}
                >
                  {loc}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        name: 'To',
        selector: (row) => row._toLoc || '',
        width: '200px',
        cell: (row) => {
          const loc = row._toLoc || '—';
          if (!loc || loc === '—') {
            return <span style={{ color: '#94A3B8' }}>—</span>;
          }
          return (
            <div style={{ width: '100%', maxWidth: '200px', minWidth: 0, overflow: 'hidden' }}>
              <Tooltip title={loc} placement="topLeft">
                <span
                  style={{
                    display: 'block',
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '12.5px',
                    color: '#334155',
                    cursor: 'pointer',
                  }}
                >
                  {loc}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        name: 'Ignition',
        selector: (row) => row._ign || '',
        width: '85px',
        cell: (row) => (
          <span
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: row.st === 'stopped' ? '#64748B' : '#16A34A',
              whiteSpace: 'nowrap',
            }}
          >
            {row._ign}
          </span>
        ),
      },
      {
        name: 'Fuel (Est.)',
        selector: (row) => row.km || 0,
        width: '95px',
        cell: (row) => (
          <span className="num" style={{ fontWeight: 700, fontSize: '12.5px', color: row._fuel === '—' ? '#94A3B8' : '#7c3aed', whiteSpace: 'nowrap' }}>
            {row._fuel}
          </span>
        ),
      },
    ],
    []
  );

  const currentPt =
    focusData && focusData.H?.pts && focusData.H.pts[playbackStep]
      ? focusData.H.pts[playbackStep]
      : focusData?.H?.pts?.[0] || null;

  const stateAtPt = (q) =>
    !q ? 'stopped' : q.spd > 0 ? 'moving' : q.ign ? 'idle' : 'stopped';

  // ==========================================
  // 1. SINGLE VEHICLE - TRACKING VIEW (Left Panel)
  // ==========================================
  if (focusedVehicle && hview === 'map') {
    if (loading) {
      return (
        <div className="lp-list" id="hpanel">
          <div className="hv-backrow">
            <button
              type="button"
              className="hv-back"
              onClick={onShowAllVehicles}
            >
              <span
                style={{ display: 'inline-grid', placeItems: 'center', width: '12px' }}
                dangerouslySetInnerHTML={{ __html: ic('chev', 'width="12" height="12"') }}
              />
              All vehicles <span className="n">{vehicles.length}</span>
            </button>
          </div>
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#6366f1' }}>
            <span className="spinner-border spinner-border-sm me-2" role="status" />
            <div style={{ marginTop: '8px', fontSize: '13px', fontWeight: '500' }}>
              Loading tracking history for {focusedVehicle.reg}...
            </div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="lp-list" id="hpanel">
          <div className="hv-backrow">
            <button
              type="button"
              className="hv-back"
              onClick={onShowAllVehicles}
            >
              <span
                style={{ display: 'inline-grid', placeItems: 'center', width: '12px' }}
                dangerouslySetInnerHTML={{ __html: ic('chev', 'width="12" height="12"') }}
              />
              All vehicles <span className="n">{vehicles.length}</span>
            </button>
          </div>
          <div style={{ padding: '40px 16px', textAlign: 'center' }}>
            <div style={{ color: '#D13B3B', marginBottom: '8px', fontSize: '13px', fontWeight: '600' }}>
              Failed to load history
            </div>
            <div style={{ color: '#7A7C96', fontSize: '12px', marginBottom: '14px' }}>
              {error}
            </div>
            {onRetry && (
              <button type="button" className="btn sm primary" onClick={onRetry}>
                Retry
              </button>
            )}
          </div>
        </div>
      );
    }

    if (!focusData) return null;
    const { v, H, segs = [], mv, idle, stp, trips = [], custs = [], fuel, km, max, avg } =
      focusData;
    const spanTotal = Math.max(1, H?.to - H?.from || 1);
    const q = currentPt || (H?.pts && H.pts.length ? H.pts[0] : null);
    const curSt = stateAtPt(q);

    return (
      <div className="lp-list" id="hpanel">
        {/* Back Link */}
        <div className="hv-backrow">
          <button
            type="button"
            className="hv-back"
            onClick={onShowAllVehicles}
          >
            <span
              style={{ display: 'inline-grid', placeItems: 'center', width: '12px' }}
              dangerouslySetInnerHTML={{ __html: ic('chev', 'width="12" height="12"') }}
            />
            All vehicles <span className="n">{vehicles.length}</span>
          </button>
        </div>

        {/* Vehicle Header & Current Playback Status */}
        <div className="hv-vrow">
          <div
            className={`hf-row hf-item hv-head ${showTrackingCard ? 'open' : ''}`}
            id="hvh"
            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
            onClick={() => {
              setShowTrackingCard((prev) => !prev);
              if (onRefreshTelemetry && focusedVehicle) {
                onRefreshTelemetry(focusedVehicle);
              }
            }}
            title="Click to toggle live vehicle tracking card & details"
          >
            <span className="mid">
              <span className="l1">
                <span
                  dangerouslySetInnerHTML={{
                    __html: typeIconSVG(v.type, focusData.endSt),
                  }}
                />
                {v.reg}
              </span>
              <span className="l2 drv-line">
                <span
                  className="drv-av"
                  style={{
                    width: '18px',
                    height: '18px',
                    background: '#E4DAFC',
                    fontSize: '8px',
                    display: 'inline-grid',
                    placeItems: 'center',
                    borderRadius: '50%',
                  }}
                >
                  {(v.drv || 'DR').slice(0, 2).toUpperCase()}
                </span>
                <span>{v.drv}</span>
              </span>
            </span>
            <span className="spd">
              <span className="hpill">{Math.round(km)} km</span>
              <br />
              <span className="num km" title="Driving time">
                <span className="km-ic">
                  <span dangerouslySetInnerHTML={{ __html: ic('clock', 'width="12" height="12"') }} />
                </span>
                {focusData.summary?.driving_time_formatted || `${dur(mv)} driving`}
              </span>
            </span>
            <span
              className="chev"
              style={{
                marginLeft: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                transition: 'transform 0.2s',
                transform: showTrackingCard ? 'rotate(180deg)' : 'none',
              }}
              dangerouslySetInnerHTML={{ __html: ic('chev', 'width="14" height="14"') }}
            />
          </div>

          {/* Inline Expanded Live Tracking Card */}
          {showTrackingCard && (
            <div style={{ margin: '6px 0 10px 0' }}>
              <VehicleTrackingCard
                vehicle={focusedVehicle}
                initialTelemetry={telemetryData}
                initialDetail={detailData}
                onTelemetryLoaded={() => {
                  if (onRefreshTelemetry) onRefreshTelemetry(focusedVehicle);
                }}
                onShowTripOnMap={onShowTripOnMap}
                onClose={() => setShowTrackingCard(false)}
              />
            </div>
          )}

          <div className="vd lv-card">
            {/* Position Readout Line */}
            <div className="lv-now" id="hnow">
              <div className="hv-pos">
                <div className="hv-pos-l">
                  <div className="lv-loc">
                    <span
                      style={{ display: 'inline-grid', placeItems: 'center', width: '13px' }}
                      dangerouslySetInnerHTML={{ __html: ic('pin', 'width="13" height="13"') }}
                    />
                    <span className="lv-place" title={q?.location || (q ? placeName(q.p) : '')}>
                      {q?.location || (q ? placeName(q.p) : 'Awaiting coordinates')}
                    </span>
                  </div>
                  <div className="lv-now-2">
                    Ignition {q ? (q.ign ? 'on' : 'off') : '—'}
                  </div>
                </div>
                <div className="hv-pos-r">
                  <span className={`spill sp-${curSt}`}>
                    {curSt.charAt(0).toUpperCase() + curSt.slice(1)}
                  </span>
                  <span className="lv-age hv-pb">
                    <b className="num">{q ? (q.time || clock(q.t)) : '--:--'}</b>
                  </span>
                  <span className="num km">
                    <span className="km-ic">
                      <span dangerouslySetInnerHTML={{ __html: ic('speedo', 'width="12" height="12"') }} />
                    </span>
                    {q?.spd ?? 0} km/h
                  </span>
                </div>
              </div>
            </div>

            {/* Trips in Period */}
            {(trips || []).length > 0 && (
              <div className="tp-sec hv-trips" id="htrips">
                <div className="hv-lbl sec-h">
                  <span>Trips in this period</span>
                </div>
                <div className="ht-list">
                  {(trips || []).map((t, idx) => (
                    <span key={idx} className="ht-c">
                      <span className={`ht-s ${t.st === 'done' ? 'cp' : 'it'}`}>
                        {t.st === 'done' ? '✓' : '●'}
                      </span>
                      <span className="ht-cm">
                        <span className="ht-l1">
                          <b>{odShort(t.from, t.to).join(' → ')}</b>
                        </span>
                        <span className="ht-l2 num">
                          {clock(t.t0)} – {t.st === 'live' ? 'now' : clock(t.t1)} ·{' '}
                          {dshort((t.t1 - t.t0) / 60000)} · {t.id}
                        </span>
                      </span>
                      <span className="ht-big num">
                        {Math.round(t.km || 0)}
                        <small>km</small>
                      </span>
                    </span>
                  ))}
                  {focusData.offKm >= 1 && (
                    <span className="ht-c off">
                      <span className="ht-s off">!</span>
                      <span className="ht-cm">
                        <span className="ht-l1">
                          <b>Moved without trip</b>
                        </span>
                        <span className="ht-l2 num">
                          {dshort(focusData.offMin)}
                        </span>
                      </span>
                      <span className="ht-big num">
                        {Math.round(focusData.offKm)}
                        <small>km</small>
                      </span>
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Period Breakdown */}
            <div className="lv-today hv-trip">
              <div className="hv-lbl sec-h">
                <span>Summary · {clock(H?.from)} – {clock(H?.to)}</span>
              </div>
              <div className="hv-big">
                <span>
                  <b className="num">{Math.round(km)}</b>
                  <small>km</small>
                </span>
                <span>
                  <b className="num">{focusData.summary?.total_time_formatted || dur((H?.to - H?.from) / 60000)}</b>
                  <small>total</small>
                </span>
              </div>
              <div className="hv-bar" role="img" aria-label="Time split">
                <span
                  style={{
                    width: `${((mv / spanTotal) * 100).toFixed(1)}%`,
                    background: STATUS_HEX.moving,
                  }}
                  title={`Driving ${focusData.summary?.driving_time_formatted || dur(mv)}`}
                />
                <span
                  style={{
                    width: `${((idle / spanTotal) * 100).toFixed(1)}%`,
                    background: STATUS_HEX.idle,
                  }}
                  title={`Idle ${focusData.summary?.idle_time_formatted || dur(idle)}`}
                />
                <span
                  style={{
                    width: `${((stp / spanTotal) * 100).toFixed(1)}%`,
                    background: STATUS_HEX.stopped,
                  }}
                  title={`Stopped ${focusData.summary?.stopped_time_formatted || dur(stp)}`}
                />
              </div>
              <div className="hv-leg one">
                <span>
                  <i style={{ background: STATUS_HEX.moving }} />
                  Driving {focusData.summary?.driving_time_formatted || dshort(mv)}
                </span>
                <span>
                  <i style={{ background: STATUS_HEX.idle }} />
                  Idle {focusData.summary?.idle_time_formatted || dshort(idle)}
                </span>
                <span>
                  <i style={{ background: STATUS_HEX.stopped }} />
                  Stopped {focusData.summary?.stopped_time_formatted || dshort(stp)} ({focusData.stops})
                </span>
              </div>
            </div>

            <div className="hv-row3 lv-row">
              <div>
                <small>Est. fuel</small>
                <b className="num">{Math.round(fuel)} L</b>
              </div>
              <div>
                <small>Avg speed</small>
                <b className="num">{avg} km/h</b>
              </div>
              <div>
                <small>Max speed</small>
                <b className="num">{max} km/h</b>
              </div>
            </div>

            <dl className="lv-kv" id="hkv">
              <dt>Vehicle type</dt>
              <dd>
                <span>{v.cat}</span> - {v.type}
              </dd>
              {focusData.summary?.total_points != null && (
                <>
                  <dt>Data points</dt>
                  <dd className="mono">{focusData.summary.total_points}</dd>
                </>
              )}
              {focusData.startPoint?.location && (
                <>
                  <dt>Start Point</dt>
                  <dd style={{ fontSize: '11px', lineHeight: '1.3' }}>
                    {focusData.startPoint.location} ({focusData.startPoint.time || clock(focusData.startPoint.recorded_at)})
                  </dd>
                </>
              )}
              {focusData.endPoint?.location && (
                <>
                  <dt>End Point</dt>
                  <dd style={{ fontSize: '11px', lineHeight: '1.3' }}>
                    {focusData.endPoint.location} ({focusData.endPoint.time || clock(focusData.endPoint.recorded_at)})
                  </dd>
                </>
              )}
              <dt>Customers</dt>
              <dd>{custs.join(', ') || v.cust || '—'}</dd>
              <dt>IMEI</dt>
              <dd className="mono">{v.dev || v.imeino}</dd>
            </dl>
          </div>
        </div>

        {/* Journey Timeline */}
        <div className="hv-sec">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h4 style={{ margin: 0 }}>
              Journey timeline <span className="upd">{segs.length} moves & stops</span>
            </h4>
            <button
              type="button"
              className="btn sm"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '11px',
                fontWeight: 600,
                color: '#4014B8',
                borderColor: '#D4CCF7',
                padding: '3px 9px',
                borderRadius: '6px',
                background: '#F8F6FF',
                cursor: exportingHistoryPdf ? 'wait' : 'pointer',
              }}
              disabled={exportingHistoryPdf}
              onClick={handleExportVehicleHistoryPdf}
              title="Download vehicle journey segments PDF"
            >
              <span dangerouslySetInnerHTML={{ __html: ic('report', 'width="12" height="12"') }} />
              {exportingHistoryPdf ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
          {segs.length === 0 && (!focusData?.trips || focusData.trips.length === 0) ? (
            <div
              style={{
                padding: '24px 16px',
                textAlign: 'center',
                background: '#FBFBFE',
                borderRadius: '8px',
                border: '1px dashed #DDD8F5',
                color: '#6B7280',
                fontSize: '12px',
                margin: '8px 0',
              }}
            >
              <div style={{ fontWeight: 600, color: '#10052E', marginBottom: '4px', fontSize: '13px' }}>
                No movement history recorded
              </div>
              <div style={{ color: '#7A7C96', lineHeight: 1.4 }}>
                No journey segments recorded for <b>{v.reg}</b> between {dt(H?.from)} and {dt(H?.to)}.
              </div>
              <div style={{ marginTop: '8px', fontSize: '11px', color: '#4014B8', fontWeight: 500 }}>
                💡 Select 7 days, 14 days, or 30 days from the top toolbar to view past movements.
              </div>
            </div>
          ) : (
            <ul className="hv-tl" id="htl">
              {(() => {
                const tripEvents = (focusData?.trips || [])
                  .flatMap((t) => [
                    { t: t.t0, k: 'start', tr: t },
                    ...(t.st === 'done' ? [{ t: t.t1, k: 'end', tr: t }] : []),
                  ])
                  .sort((a, b) => a.t - b.t);

                const items = [];
                let ei = 0;

                segs.forEach((g, idx) => {
                  // Trip events that happened before this segment ends
                  while (ei < tripEvents.length && tripEvents[ei].t < g.t1 - 30000) {
                    const ev = tripEvents[ei++];
                    items.push(
                      <li key={`ev-${ev.k}-${ev.t}`} className={`hv-ev ${ev.k}`}>
                        <span className="tm num">
                          {clock(ev.k === 'start' ? (ev.tr.rawStart || ev.t) : ev.t)}
                        </span>
                        <span className="td" />
                        <span className="tt">
                          <b>{ev.k === 'start' ? `▶ Trip ${ev.tr.id} started` : `✓ Trip ${ev.tr.id} completed`}</b>
                          <small>
                            {ev.k === 'start' ? (
                              <>
                                {ev.tr.from} → {ev.tr.to}
                                {ev.tr.late >= 10 && <span className="late"> +{dshort(ev.tr.late)} late</span>}
                              </>
                            ) : (
                              `Reached ${ev.tr.to}`
                            )}
                          </small>
                        </span>
                      </li>
                    );
                  }

                  const isOpen = openSegIdx === idx;
                  const isMoving = g.st === 'moving';
                  const isIdle = g.st === 'idle';

                  items.push(
                    <li key={`seg-${idx}`} className={isOpen ? 'open' : ''}>
                      <button
                        type="button"
                        className="hv-tl-row"
                        onClick={() => {
                          setOpenSegIdx(isOpen ? -1 : idx);
                          if (onSeekPlayback && g.n0 !== undefined) {
                            onSeekPlayback(g.n0);
                          }
                        }}
                      >
                        <span className="tm num">
                          {clock(g.t0)}
                          <small>{clock(g.t1)}</small>
                        </span>
                        <span
                          className="td"
                          style={{
                            background: isMoving
                              ? STATUS_HEX.moving
                              : isIdle
                              ? STATUS_HEX.idle
                              : STATUS_HEX.stopped,
                          }}
                        />
                        <span className="tt">
                          <b>{isMoving ? 'Driving' : isIdle ? 'Idle' : 'Stopped'}</b> ·{' '}
                          {dur(g.min)}
                          {isMoving ? ` · ${Math.round(g.km)} km` : ''}
                          <small>{getSegStartLoc(g, H)}</small>
                        </span>
                        <span
                          className="hv-chev"
                          dangerouslySetInnerHTML={{ __html: ic('chev') }}
                        />
                      </button>

                      {isOpen && (
                        <div className="hv-seg2">
                          {isMoving ? (
                            <>
                              <div className="sg-tiles">
                                <div className="sg-tile">
                                  <small>Distance</small>
                                  <b className="num">{Math.round(g.km)} km</b>
                                </div>
                                <div className="sg-tile">
                                  <small>Avg speed</small>
                                  <b className="num">{g.avg} km/h</b>
                                </div>
                                <div className="sg-tile">
                                  <small>Max speed</small>
                                  <b className={`num ${g.max > SPEED_LIMIT[v.cat] ? 'over' : ''}`}>
                                    {g.max} km/h
                                  </b>
                                </div>
                              </div>
                              <div className="sg-ft">
                                <span className="sg-pin a" />
                                <span className="sg-pl">{getSegStartLoc(g, H)}</span>
                                <span className="sg-t num">{clock(g.t0)}</span>
                                <span className="sg-vl" />
                                <span />
                                <span />
                                <span className="sg-pin b" />
                                <span className="sg-pl">{getSegEndLoc(g, H)}</span>
                                <span className="sg-t num">{clock(g.t1)}</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="sg-tiles">
                                <div className="sg-tile">
                                  <small>Duration</small>
                                  <b className="num">{dur(g.min)}</b>
                                </div>
                                <div className="sg-tile">
                                  <small>Ignition</small>
                                  <b className={`num ${isIdle ? 'on' : 'off'}`}>
                                    {isIdle ? 'On' : 'Off'}
                                  </b>
                                </div>
                                <div className="sg-tile">
                                  <small>{isIdle ? 'Idle fuel' : 'Since'}</small>
                                  <b className="num">
                                    {isIdle
                                      ? FUEL_CONFIG.idleEnabled
                                        ? `${((FUEL_CONFIG.idleLph[v.cat || 'HTV'] * g.min) / 60).toFixed(1)} L`
                                        : '—'
                                      : clock(g.t0)}
                                  </b>
                                </div>
                              </div>
                              <div
                                className="sg-ft one"
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '10px minmax(0,1fr) auto',
                                  gap: '0 7px',
                                  alignItems: 'start',
                                }}
                              >
                                <span
                                  className="sg-pin"
                                  style={{
                                    background: isIdle
                                      ? STATUS_HEX.idle
                                      : STATUS_HEX.stopped,
                                  }}
                                />
                                <span className="sg-pl">{getSegStartLoc(g, H)}</span>
                                <span className="sg-t num">
                                  {clock(g.t0)} – {clock(g.t1)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </li>
                  );
                });

                // Remaining trip events
                while (ei < tripEvents.length) {
                  const ev = tripEvents[ei++];
                  items.push(
                    <li key={`ev-rem-${ev.k}-${ev.t}`} className={`hv-ev ${ev.k}`}>
                      <span className="tm num">{clock(ev.t)}</span>
                      <span className="td" />
                      <span className="tt">
                        <b>{ev.k === 'start' ? `▶ Trip ${ev.tr.id} started` : `✓ Trip ${ev.tr.id} completed`}</b>
                        <small>Reached {ev.tr.to}</small>
                      </span>
                    </li>
                  );
                }

                return items;
              })()}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // ==========================================
  // 2. FLEET - TRACKING VIEW (Left Panel List)
  // ==========================================
  if (!focusedVehicle && hview === 'map') {
    return (
      <div className="lp-list" id="hfpanel">
        <div className="hv-sec">
          <h4>
            Vehicles <span className="upd">{fleetDataList.length} · sorted by distance</span>
          </h4>
          <ul className="hf-list">
            {[...fleetDataList]
              .sort((a, b) => b.km - a.km)
              .map((d, sIdx) => {
                const v = d.v || {};
                const regKey = v.reg || v.registration_number || `veh-${sIdx}`;
                return (
                  <li key={regKey}>
                    <button
                      type="button"
                      className={`hf-row hf-item ${hoveredReg === regKey ? 'hi' : ''}`}
                      onClick={() => {
                        if (onSelectVehicle) onSelectVehicle(regKey);
                        if (onRefreshTelemetry) onRefreshTelemetry(v);
                      }}
                      onMouseEnter={() => onHoverReg && onHoverReg(regKey)}
                      onMouseLeave={() => onHoverReg && onHoverReg(null)}
                    >
                      <span className="mid">
                        <span className="l1">
                          <span
                            dangerouslySetInnerHTML={{
                              __html: typeIconSVG(v.type, d.endSt),
                            }}
                          />
                          {v.reg}
                        </span>
                        <span className="l2 drv-line">
                          <span
                            className="drv-av"
                            style={{
                              width: '18px',
                              height: '18px',
                              background: '#E4DAFC',
                              fontSize: '8px',
                              display: 'inline-grid',
                              placeItems: 'center',
                              borderRadius: '50%',
                            }}
                          >
                            {v.drv.slice(0, 2).toUpperCase()}
                          </span>
                          <span>{v.drv}</span>
                        </span>
                        <span className="trip-tag t-nt hf-stops">
                          <i />
                          {d.stops} {d.stops === 1 ? 'stop' : 'stops'} · idle {dshort(d.idle)}
                        </span>
                      </span>

                      <span className="spd">
                        <span className="hpill">
                          {Math.round(d.km).toLocaleString('en-US')} km
                        </span>
                        <br />
                        <span className="num km" title="Driving time">
                          <span className="km-ic">
                            <span dangerouslySetInnerHTML={{ __html: ic('clock', 'width="12" height="12"') }} />
                          </span>
                          {dur(d.mv)} driving
                        </span>
                        <br />
                        <span className="num km hf-avg">
                          <span className="km-ic">
                            <span dangerouslySetInnerHTML={{ __html: ic('speedo', 'width="12" height="12"') }} />
                          </span>
                          {d.avg} / {d.max} km/h
                        </span>
                      </span>

                      {/* Journey timeline mini bar */}
                      <span className="jstrip">
                        <span className="js-bar">
                          {(d.runs || []).map((r, rIdx) => {
                            const span = Math.max(1, (d.H?.to || 0) - (d.H?.from || 0));
                            const w = (((r.t1 - r.t0) / span) * 100).toFixed(1);
                            return (
                              <span
                                key={rIdx}
                                style={{
                                  width: `${w}%`,
                                  background: RUN_HEX[r.k] || '#4014B8',
                                }}
                              />
                            );
                          })}
                        </span>
                        <span className="js-ends">
                          <span className="js-a">
                            <b className="num">{d.H?.from ? clock(d.H.from) : '—'}</b>
                            <span className="js-p">{d.H?.pts?.[0]?.p ? nearName(d.H.pts[0].p) : (d.v?.loc || '—')}</span>
                          </span>
                          <span className="js-b">
                            <span className="js-p">{d.endP ? nearName(d.endP) : (d.v?.loc || '—')}</span>
                            <b className="num">{d.H?.to ? clock(d.H.to) : '—'}</b>
                          </span>
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
          </ul>
        </div>
      </div>
    );
  }

  // ==========================================
  // 3. SINGLE VEHICLE - LISTING VIEW (Table)
  // ==========================================
  if (focusedVehicle && hview === 'list') {
    const v = focusData?.v || focusedVehicle || {};
    const H = focusData?.H || {};
    const segs = focusData?.segs || [];

    return (
      <div className="lt-scroll hl-wrap" id="hfleet" style={{ paddingBottom: '90px' }}>
        <div style={{ marginBottom: '10px' }}>
          <button
            type="button"
            className="hv-back"
            onClick={onShowAllVehicles}
          >
            <span
              style={{ display: 'inline-grid', placeItems: 'center', width: '12px' }}
              dangerouslySetInnerHTML={{ __html: ic('chev', 'width="12" height="12"') }}
            />
            All vehicles <span className="n">{vehicles.length}</span>
          </button>
        </div>

        <div className="card hl-card">
          <div className="card-h" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3>Trip segments · {v.reg || v.registration_number || '-'}</h3>
              <span className="sub">
                {segs.length} segments{H.from ? ` · ${dt(H.from)} – ${dt(H.to)}` : ''}
              </span>
            </div>
            <button
              type="button"
              className="btn sm"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '11px',
                fontWeight: 600,
                color: '#4014B8',
                borderColor: '#D4CCF7',
                padding: '4px 10px',
                borderRadius: '6px',
                background: '#F8F6FF',
                cursor: exportingHistoryPdf ? 'wait' : 'pointer',
              }}
              disabled={exportingHistoryPdf}
              onClick={handleExportVehicleHistoryPdf}
              title="Download vehicle journey segments PDF"
            >
              <span dangerouslySetInnerHTML={{ __html: ic('report', 'width="12" height="12"') }} />
              {exportingHistoryPdf ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
          {error ? (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <div style={{ color: '#EF4444', marginBottom: '12px', fontWeight: 600 }}>{error}</div>
              {onRetry && (
                <button type="button" className="btn sm primary" onClick={onRetry}>
                  Retry
                </button>
              )}
            </div>
          ) : (
            <NewTableComponent
              showAddBtn={false}
              showFilter={false}
              columns={segmentColumns}
              data={paginatedSegs}
              currentPage={segCurrentPage}
              setCurrentPage={setSegCurrentPage}
              limit={segLimit}
              setLimit={(nextLimit) => {
                setSegLimit(nextLimit);
                setSegCurrentPage(1);
              }}
              totalItems={segTotalItems}
              totalPages={segTotalPages}
              loading={loading}
              paginationLoading={false}
              limitOptions={[10, 20, 50, 100]}
              showPagination={true}
              expandableRows={false}
              fixedHeader={false}
              tableClassName="parts_table_fix vehicle_history_table"
              noDataComponent={
                <div style={{ textAlign: 'center', padding: '36px 16px', color: '#6B7280' }}>
                  <div style={{ fontWeight: 600, color: '#10052E', marginBottom: '4px', fontSize: '13px' }}>
                    No trip segments recorded
                  </div>
                  <div style={{ color: '#7A7C96', fontSize: '12px' }}>
                    No movement segments recorded for <b>{v.reg || '-'}</b> between {dt(H?.from)} and {dt(H?.to)}.
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#4014B8', fontWeight: 500 }}>
                    💡 Select 7 days, 14 days, or 30 days from the top toolbar to view past movements.
                  </div>
                </div>
              }
            />
          )}
        </div>
      </div>
    );
  }

  // ==========================================
  // 4. FLEET - LISTING VIEW (Master Table)
  // ==========================================
  return (
    <div className="lt-scroll hl-wrap" id="hfleet" style={{ paddingBottom: '90px' }}>
      <div className="card hl-card">
        <div className="card-h">
          <h3>Vehicle history</h3>
          <span className="sub">
            {fleetDataList.length} vehicles
          </span>
        </div>
        {error ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ color: '#EF4444', marginBottom: '12px', fontWeight: 600 }}>{error}</div>
            {onRetry && (
              <button type="button" className="btn sm primary" onClick={onRetry}>
                Retry
              </button>
            )}
          </div>
        ) : (
          <NewTableComponent
            showAddBtn={false}
            showFilter={false}
            columns={fleetColumns}
            data={paginatedFleetList}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            limit={limit}
            setLimit={(nextLimit) => {
              setLimit(nextLimit);
              setCurrentPage(1);
            }}
            totalItems={totalItems}
            totalPages={totalPages}
            loading={loading}
            paginationLoading={false}
            limitOptions={[10, 20, 50, 100]}
            showPagination={true}
            expandableRows={false}
            fixedHeader={false}
            stickyColumns={0}
            tableClassName="parts_table_fix vehicle_history_table"
          />
        )}
      </div>
    </div>
  );
};

export default VehicleTrackingHistory;
